import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, HeartHandshake, Sparkles, Users, ArrowRight, CheckCircle2, Loader2, Phone } from "lucide-react";

const GOLD = "#c9a961";
const HERO = "https://images.unsplash.com/photo-1648634362534-238cb091708b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const APPROCHE_IMG = "https://images.unsplash.com/photo-1590556409324-aa1d726e5c3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const PILIERS = [
  { icon: ShieldCheck, t: "Réflexes concrets", d: "Des techniques simples, efficaces et faciles à retenir : prévention, dégagements et gestes de protection." },
  { icon: HeartHandshake, t: "Approche bienveillante", d: "Cadre sécuritaire, sans jugement, respectueux du rythme de chacune — pensé pour les femmes ayant vécu de la violence." },
  { icon: Sparkles, t: "Reprendre son pouvoir", d: "Confiance, conscience de soi et gestion du stress : redonner de la force intérieure autant que physique." },
];

const PROGRAMME = [
  "Conscience situationnelle & prévention",
  "Gestes de dégagement de base",
  "Voix, posture et limites",
  "Gestion du stress et de la peur",
  "Mises en situation adaptées et volontaires",
  "Confiance corporelle & estime de soi",
];

export default function AutodefenseFemmes() {
  const [form, setForm] = useState({
    org_name: "", contact_name: "", email: "", phone: "", city: "",
    participants: "", format: "seance_unique", period: "", location: "sur_place", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error("Organisme, nom et courriel sont requis"); return;
    }
    setBusy(true);
    try {
      const r = await api.post("/autodefense-femmes/request", form);
      setDone(true);
      toast.success(r.data.message || "Demande envoyée !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="autodefense-femmes-page">
      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-center overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/30" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20">
          <span className="inline-block text-xs uppercase tracking-[0.35em] mb-4" style={{ color: GOLD }}>
            Programme régional · Abitibi-Témiscamingue
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">
            Autodéfense pour femmes
            <span className="block mt-2" style={{ color: GOLD }}>Pouvoir & Sécurité</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-2xl">
            Une formation concrète, humaine et sécurisante pour redonner aux femmes confiance, réflexes
            et pouvoir personnel. Offerte aux organismes de la région par Kru Ralph — 25+ ans d'expérience.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#demande" data-testid="hero-cta-demande"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-zinc-950"
              style={{ backgroundColor: GOLD }}>
              Organiser une formation <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#programme" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 hover:bg-white/5 transition-colors">
              Découvrir le programme
            </a>
          </div>
        </div>
      </section>

      {/* PILIERS */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {PILIERS.map((p, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-zinc-900/50 p-7 hover:border-amber-500/40 transition-colors" data-testid={`pilier-${i}`}>
              <p.icon className="w-8 h-8 mb-4" style={{ color: GOLD }} />
              <h3 className="text-lg font-semibold mb-2">{p.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMME + APPROCHE */}
      <section id="programme" className="border-y border-white/10 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: GOLD }}>Le programme</span>
            <h2 className="text-3xl font-bold mt-2 mb-6">Ce que les participantes apprennent</h2>
            <ul className="space-y-3">
              {PROGRAMME.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-300">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
              <p className="text-sm text-amber-100/90">
                <strong>Approche trauma-informed.</strong> Aucune mise en situation imposée. Tout se fait
                au rythme des participantes, dans un climat de respect et de sécurité émotionnelle.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src={APPROCHE_IMG} alt="" className="rounded-2xl w-full h-[420px] object-cover border border-white/10" />
          </div>
        </div>
      </section>

      {/* EXPERTISE */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20 text-center">
        <Users className="w-10 h-10 mx-auto mb-4" style={{ color: GOLD }} />
        <h2 className="text-3xl font-bold mb-4">Une expertise reconnue en région</h2>
        <p className="text-zinc-400 max-w-3xl mx-auto leading-relaxed">
          Kru Ralph David Jean-Pierre cumule plus de <strong className="text-white">25 ans d'expérience</strong> en
          arts martiaux et autodéfense, dont plus de <strong className="text-white">15 ans en Abitibi-Témiscamingue</strong>.
          Ce programme a déjà été déployé avec la <strong className="text-white">Table de concertation de
          l'Abitibi-Témiscamingue</strong>, avec d'excellents résultats. Aujourd'hui, avec de meilleurs outils,
          nous le relançons pour bâtir une vraie collaboration et un suivi durable avec les organismes de la région.
        </p>
      </section>

      {/* DEMANDE / FORMULAIRE */}
      <section id="demande" className="border-t border-white/10 bg-zinc-900/40">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-[0.3em]" style={{ color: GOLD }}>Pour les organismes</span>
            <h2 className="text-3xl font-bold mt-2">Organiser une formation</h2>
            <p className="text-zinc-400 mt-3">Centres de femmes, maisons d'hébergement, écoles, milieux communautaires — parlons-en.</p>
          </div>

          {/* PREUVE SOCIALE — citation/partenariat (éditable par Kru Ralph) */}
          <figure data-testid="autodefense-social-proof" className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 mb-8">
            <blockquote className="text-lg sm:text-xl leading-relaxed text-zinc-100">
              « Un programme concret et bienveillant, qui redonne confiance et outille réellement les
              femmes. C'est exactement le type de collaboration qui a fait sa preuve dans la région. »
            </blockquote>
            <figcaption className="mt-4 text-sm text-zinc-400">
              — En collaboration avec la <strong className="text-zinc-200">Table de concertation de l'Abitibi-Témiscamingue</strong> · Approche trauma-informed
            </figcaption>
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              {["25+ ans d'expérience", "15+ ans en région", "Approche 100% bienveillante", "Sur place ou au dojo"].map((b) => (
                <span key={b} className="px-3 py-1.5 rounded-full border border-white/15 text-zinc-300" style={{ borderColor: `${GOLD}40` }}>{b}</span>
              ))}
            </div>
          </figure>


          {done ? (
            <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-10 text-center" data-testid="autodefense-success">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: GOLD }} />
              <h3 className="text-2xl font-bold text-amber-200">Merci, votre demande est envoyée !</h3>
              <p className="text-zinc-300 mt-3">Kru Ralph vous contactera rapidement pour bâtir une proposition sur mesure.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="autodefense-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom de l'organisme *" v={form.org_name} on={(x) => setForm({ ...form, org_name: x })} tid="af-org" />
                <Field label="Votre nom *" v={form.contact_name} on={(x) => setForm({ ...form, contact_name: x })} tid="af-contact" />
                <Field label="Courriel *" type="email" v={form.email} on={(x) => setForm({ ...form, email: x })} tid="af-email" />
                <Field label="Téléphone" v={form.phone} on={(x) => setForm({ ...form, phone: x })} tid="af-phone" />
                <Field label="Ville" v={form.city} on={(x) => setForm({ ...form, city: x })} tid="af-city" />
                <Field label="Nombre de participantes (approx.)" v={form.participants} on={(x) => setForm({ ...form, participants: x })} tid="af-participants" />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Select label="Format" v={form.format} on={(x) => setForm({ ...form, format: x })} tid="af-format"
                  opts={[["seance_unique", "Séance unique"], ["serie", "Série de rencontres"], ["continue", "Formation continue"]]} />
                <Select label="Lieu" v={form.location} on={(x) => setForm({ ...form, location: x })} tid="af-location"
                  opts={[["sur_place", "Sur place (chez vous)"], ["au_dojo", "Au dojo (Val-d'Or)"]]} />
                <Field label="Période visée" v={form.period} on={(x) => setForm({ ...form, period: x })} tid="af-period" placeholder="ex. automne 2026" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">Message</label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4} data-testid="af-message"
                  className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 focus:border-amber-500 outline-none" />
              </div>
              <button type="submit" disabled={busy} data-testid="af-submit"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-zinc-950 disabled:opacity-50"
                style={{ backgroundColor: GOLD }}>
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Envoyer ma demande <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-center text-zinc-500 text-xs flex items-center justify-center gap-1.5">
                <Phone className="w-3 h-3" /> Vous préférez parler ? Réception 24/7 : 819-975-7881
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, v, on, type = "text", tid, placeholder = "" }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">{label}</label>
      <input type={type} value={v} placeholder={placeholder} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 focus:border-amber-500 outline-none" />
    </div>
  );
}

function Select({ label, v, on, opts, tid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1">{label}</label>
      <select value={v} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 focus:border-amber-500 outline-none">
        {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
      </select>
    </div>
  );
}
